import Cookies from 'js-cookie';
import { jwtVerify, createRemoteJWKSet, JWTPayload } from 'jose';
import { getAuthServiceUrl, getGuestServiceUrl, getGuestJwksUrl } from './url';
import { isCiamEnabled, isGuestTokenEnabled } from './retailUnitsWithCiam';
import { Cookie } from '../network/enums';
import { setupSession } from '../network/axios';
import { stringToBoolean } from '@mvecom/common/utils/stringToBoolean';
import { Authentication } from '../network/types';
import { getRetailUnitAndLanguage } from '../network/utils/getRetailUnitAndLanguage';
import { getCookieDomain } from '../network/utils/getCookieDomain';
import { getRetailUnitCookiePath } from '../network/utils/getRetailUnitCookiePath';

const { retailUnit } = getRetailUnitAndLanguage();
const AUTH0_COOKIE = `auth0-session-${retailUnit}`;

// JWKS endpoint for guest token verification (environment-aware)
const JWKS = createRemoteJWKSet(new URL(getGuestJwksUrl()));

function getAuthTokenPayloadFromCookie(): JWTPayload | undefined {
  const cookieValue = Cookies.get(AUTH0_COOKIE);
  if (cookieValue) {
    const [, payload] = cookieValue.split('.');
    if (payload) {
      const jsonToken = JSON.parse(atob(payload));
      return jsonToken;
    }
  }
}

async function verifyGuestToken(token: string): Promise<JWTPayload | undefined> {
  try {
    const { payload } = await jwtVerify(token, JWKS);
    return payload;
  } catch {
    return undefined;
  }
}

function isTokenExpired(payload: JWTPayload) {
  const exp = (payload.exp ?? 0) * 1000;
  return new Date(exp) < new Date();
}

function isTokenAboutToExpire(payload: JWTPayload) {
  const fifteenMinutes = 15 * 60 * 1000;
  return (payload.exp ?? 0) * 1000 - Date.now() < fifteenMinutes;
}

export function hasWCCookies(): boolean {
  const all = Cookies.get();
  return Object.keys(all ?? {}).some((key) => key.startsWith('WC_'));
}

export function authHelper(): typeof globalThis.ikea.mvAgent.authentication {
  // Only one simultaneous isLoggedIn call is permitted
  let isLoggedInPromise: Promise<Authentication>;
  let pending = false;
  const provider = isCiamEnabled ? 'ciam' : 'icaps';

  // Guest token state
  let guestToken: string | undefined;
  let guestTokenPromise: Promise<string> | null = null;

  async function fetchNewGuestToken(): Promise<string> {
    const res = await fetch(`${getGuestServiceUrl()}/token/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ retailUnit }),
    });

    if (!res.ok) {
      throw new Error(`Failed to fetch guest token: ${res.status}`);
    }

    const data = await res.json();

    if (!Cookies.get(Cookie.FAVOURITES_GUEST_PROVIDER)) {
      Cookies.set(Cookie.FAVOURITES_GUEST_PROVIDER, 'icaps', {
        domain: getCookieDomain,
        path: getRetailUnitCookiePath,
      });
    }
    if (!Cookies.get(Cookie.CART_GUEST_PROVIDER)) {
      Cookies.set(Cookie.CART_GUEST_PROVIDER, 'icaps', {
        domain: getCookieDomain,
        path: getRetailUnitCookiePath,
      });
    }

    return data.access_token;
  }

  async function refreshGuestToken(token: string): Promise<string> {
    const res = await fetch(`${getGuestServiceUrl()}/refreshtoken/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ token }),
    });

    if (!res.ok) {
      throw new Error(`Failed to refresh guest token: ${res.status}`);
    }

    const data = await res.json();

    return data.access_token;
  }

  async function getOrRefreshGuestToken(): Promise<string> {
    guestTokenPromise ??= (async () => {
      try {
        if (guestToken) {
          const payload = await verifyGuestToken(guestToken);
          if (payload && isTokenAboutToExpire(payload)) {
            guestToken = await refreshGuestToken(guestToken);
          } else if (!payload) {
            guestToken = await fetchNewGuestToken();
          }
        } else {
          guestToken = await fetchNewGuestToken();
        }

        if (!guestToken) {
          throw new Error('Guest token is undefined after fetch');
        }

        return guestToken;
      } catch (error) {
        console.error('Failed to get or refresh guest token:', error);
        throw error;
      } finally {
        guestTokenPromise = null;
      }
    })();
    return guestTokenPromise;
  }

  const isLoggedIn = async (): Promise<Authentication> => {
    if (!isCiamEnabled) {
      if (!Cookies.get(Cookie.USER_LOGGEDIN)) {
        await setupSession();
      }
      const isLoggedIn = stringToBoolean(Cookies.get(Cookie.USER_LOGGEDIN));
      return { isLoggedIn, authToken: undefined, guestToken: undefined, guestId: undefined, provider: 'icaps' };
    }

    const tokenPayload = getAuthTokenPayloadFromCookie();

    // If no auth token and guest tokens are disabled, use old session flow for guests
    if (!tokenPayload && !isGuestTokenEnabled) {
      if (!Cookies.get(Cookie.USER_LOGGEDIN)) {
        await setupSession();
      }
      const isLoggedIn = stringToBoolean(Cookies.get(Cookie.USER_LOGGEDIN));
      return { isLoggedIn, authToken: undefined, guestToken: undefined, guestId: undefined, provider: 'icaps' };
    }

    let guest: string | undefined;
    let guestId: string | undefined;
    if (isGuestTokenEnabled && !hasWCCookies()) {
      try {
        guest = await getOrRefreshGuestToken();
        if (guest) {
          const guestPayload = await verifyGuestToken(guest);
          guestId = guestPayload?.guestId as string | undefined;
        }
      } catch (error) {
        console.error('Failed to get guest token in isLoggedIn:', error);
        // Continue without guest token
      }
    }

    if (!tokenPayload) {
      return { isLoggedIn: false, authToken: undefined, guestToken: guest, guestId, provider };
    }

    if (isTokenExpired(tokenPayload)) {
      const response = await fetch(`${getAuthServiceUrl()}/status/`);
      const { isAuthenticated } = (await response.json()) as { isAuthenticated: boolean };
      if (!isAuthenticated) {
        return { isLoggedIn: false, authToken: undefined, guestToken: guest, guestId, provider };
      }
    } else if (isTokenAboutToExpire(tokenPayload)) {
      await fetch(`${getAuthServiceUrl()}/refresh/`);
    }

    return {
      isLoggedIn: true,
      authToken: Cookies.get(AUTH0_COOKIE),
      guestToken: guest,
      guestId,
      provider,
    };
  };

  return {
    getTokens: async () => {
      let guest: { value: string; expirationTime?: number; userId?: string } | undefined;
      if (isGuestTokenEnabled && !hasWCCookies()) {
        try {
          const token = await getOrRefreshGuestToken();
          if (token) {
            const payload = await verifyGuestToken(token);
            guest = { value: token, expirationTime: payload?.exp, userId: payload?.guestId as string | undefined };
          }
        } catch (error) {
          console.error('Failed to get guest token in getTokens:', error);
          // Continue without guest token
        }
      }

      const tokenPayload = getAuthTokenPayloadFromCookie();
      if (!tokenPayload) {
        return {
          auth: { value: undefined, expirationTime: undefined },
          ...(guest && { guest }),
        };
      }

      if (isTokenExpired(tokenPayload)) {
        await fetch(`${getAuthServiceUrl()}/status/`);
      } else if (isTokenAboutToExpire(tokenPayload)) {
        await fetch(`${getAuthServiceUrl()}/refresh/`);
      }

      return {
        auth: { value: Cookies.get(AUTH0_COOKIE), expirationTime: getAuthTokenPayloadFromCookie()?.exp },
        ...(guest && { guest }),
      };
    },
    isLoggedIn: async (force = false) => {
      if (!pending || force) {
        isLoggedInPromise = isLoggedIn();
        pending = true;
      }
      const out = await isLoggedInPromise;
      pending = false;
      return out;
    },
  };
}
